import { Injectable } from '@angular/core';
import { authOnce } from '@app/authentification/auth-once';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { UserAuthentified } from '@app/authentification/user-authentified.interface';
import { Iban } from '@app/dao/user/modals';
import { User } from '@app/models/user';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private httpApi: HttpApiService,
    private authentificationService: AuthentificationService,
  ) {}

  /**
   * Flux VIF : ne complète jamais. Même flux que Dao.getAuthentified().
   * @deprecated pour les lectures one-shot → utiliser authOnce$().
   */
  getUser(): Observable<UserAuthentified> {
    return this.authentificationService.authentication;
  }

  /**
   * Instantané one-shot de l'utilisateur courant : émet une fois, puis
   * complète.
   */
  authOnce$(): Observable<UserAuthentified> {
    return authOnce(this.authentificationService.authentication);
  }

  resetUser(): void {
    this.authentificationService.resetAuthentication();
  }

  // TODO: user.service should be split in two services: one to manage the current user's data, another to query user related /api endpoints

  updateUser(profile: User): any {
    return this.httpApi.post('/user', profile);
  }

  deleteUser(userId: any): any {
    return this.httpApi.delete(`/user/${userId}`);
  }

  getRelation(userId: any, type: string = 'spouse'): Observable<any> {
    return this.httpApi.get(`/user/${userId}/relation/${type}`);
  }

  updateRelation(userId: any, type: string = 'spouse', body: any): any {
    return this.httpApi.post(`/user/${userId}/relation/${type}`, body);
  }

  deleteRelation(userId: any, id: any): Observable<any> {
    return this.httpApi.delete(`/user/${userId}/relation/${id}`);
  }

  getCompanies(userId: any): Observable<any> {
    return this.httpApi.get(`/user/${userId}/companies`);
  }

  getCompany(userId: any, id: any): Observable<any> {
    return this.httpApi.get(`/user/${userId}/companies/${id}`);
  }

  createCompany(userId: any, body: any): Observable<any> {
    return this.httpApi.post(`/user/${userId}/companies`, body);
  }

  updateCompany(userId: any, id: any, body: any): Observable<any> {
    return this.httpApi.put(`/user/${userId}/companies/${id}`, body);
  }

  deleteCompany(userId: any, id: any): Observable<any> {
    return this.httpApi.delete(`/user/${userId}/companies/${id}`);
  }

  updateTaxHouse(
    userId: any,
    year: any = new Date().getFullYear(),
    body: any,
  ): any {
    return this.httpApi.post(`/user/${userId}/taxhouse/info/${year}`, body);
  }

  getTaxesHouse(userId: any, year: any = new Date().getFullYear()): any {
    return this.httpApi.get(`/user/${userId}/taxhouse/info/${year}/global`);
  }

  updateTaxesHouse(
    userId: any,
    year: any = new Date().getFullYear(),
    body: any,
  ) {
    return this.httpApi.post(`/user/${userId}/taxhouse/info/${year}`, body);
  }

  getMarriageRegime(userId: any): any {
    return this.httpApi.get(`/user/${userId}/relation/current/marriageRegime`);
  }

  updateMarriageRegime(userId: any, body: any): any {
    return this.httpApi.post(
      `/user/${userId}/relation/current/marriageRegime`,
      body,
    );
  }

  getSameTaxHouse(userId: any): Observable<any> {
    return this.httpApi.get(`/user/${userId}/relation/sameTaxHousehold`);
  }

  updateSameTaxHouse(userId: any, body: any): Observable<any> {
    return this.httpApi.post(`/user/${userId}/relation/sameTaxHousehold`, body);
  }

  updateDeductionsOrReduction(
    userId: any,
    model: any,
    body: any,
    year: any = new Date().getFullYear(),
  ): any {
    return this.httpApi.post(
      `/user/${userId}/taxhouse/info/${year}/${model}`,
      body,
    );
  }

  getAddresses(userId: any): Observable<any> {
    return this.httpApi.get(`/user/${userId}/address`);
  }

  updateAddress(userId: any, body: any): any {
    return this.httpApi.post(`/user/${userId}/address`, body);
  }

  updateContact(userId: any, body: any): any {
    return this.httpApi.post(`/user/${userId}/contact`, body);
  }

  requestContact(userId: any, body: any): Observable<any> {
    return this.httpApi.post(`/user/${userId}/message/product/info`, body);
  }

  isLoggedin(): boolean {
    return this.authentificationService.hasValidToken();
  }

  activation(key: string): any {
    return this.httpApi.get(`/activate?key=${key}`);
  }

  reset(email: string): Observable<any> {
    return this.httpApi.post('/account/reset_password/init', email);
  }

  resetPassword(keyPass: any): Observable<any> {
    return this.httpApi.post('/accounts/reset_password', keyPass);
  }

  initPassword(keyPass: any): Observable<any> {
    return this.httpApi.post('/accounts/init_password', keyPass);
  }

  initializePassword(
    accountId: any,
    userAccountId: any,
    body: any,
  ): Observable<any> {
    return this.httpApi.post(
      `/accounts/${accountId}/reset-password?userAccountId=${userAccountId}`,
      body,
    );
  }

  autoLogin(token: any): Observable<any> {
    return this.httpApi.post('/account/init_password/init/autologin', token);
  }

  changePassword(accountId: any, body: any): any {
    return this.httpApi.post(`/accounts/${accountId}/change-password`, body);
  }

  removeAccount(accountId: any, body: any): any {
    return this.httpApi.post(`/accounts/${accountId}/remove-account`, body);
  }

  getRiskProfile(userId: any): any {
    return this.httpApi.get(`/owner/${userId}/risk-profile`);
  }

  getOwnerIbans(accountId: number): Observable<Iban[]> {
    return this.httpApi.get(`/accounts/${accountId}/ownerIban`);
  }

  saveOwnerIban(accountId: number, body: any): Observable<any> {
    return this.httpApi.post(`/accounts/${accountId}/ownerIban`, body);
  }

  saveUser(entity: any): Observable<any> {
    let dto = { ...entity };
    return this.httpApi.post(`/accounts/register`, dto);
  }
}
